<script setup>
/* global axios */
// Caixa de Flow na conversa — dropdown para associar o ticket a uma Sequência (ao selecionar
// já salva/enrola); botão para ver o percurso do cliente (popup visual) e uma frase com o nó atual.
import { ref, computed, onMounted, nextTick, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
// lazy: o FlowJourneyModal puxa @vue-flow (lib pesada). Como o ContactPanel abre em
// TODA conversa, carregar sob demanda evita baixar @vue-flow pra quem nunca abre a jornada.
const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const FlowJourneyModal = defineAsyncComponent(
  () => import('../kanban/flow/FlowJourneyModal.vue')
);

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const sequences = ref([]);
const activeExec = ref(null); // execução em andamento desta conversa
const selectedFlowId = ref('');
const flowsById = ref({});
const saving = ref(false);
const loading = ref(true);
const showJourney = ref(false);

const activeFlow = computed(
  () => flowsById.value[selectedFlowId.value] || null
);
const currentNodeLabel = computed(() => {
  if (!activeExec.value || !activeFlow.value) return null;
  const nodes = (activeFlow.value.flow_data || {}).nodes || [];
  const n = nodes.find(x => x.id === activeExec.value.current_node_id);
  if (!n) return null;
  return n.data?.title || t(`CHATBOT.nodes.${n.data?.nodeType}.label`);
});
const statusKey = computed(() => activeExec.value?.status);

const loadAll = async () => {
  loading.value = true;
  try {
    const flows = (await axios.get(`${base}/flows`)).data.data || [];
    sequences.value = flows.filter(
      f => f.flow_type === 'sequence' && f.is_active
    );
    sequences.value.forEach(f => {
      flowsById.value[f.id] = f;
    });
    const execs =
      (
        await axios.get(`${base}/flow_executions`, {
          params: { conversation_id: props.conversationId },
        })
      ).data.data || [];
    // execução ativa preferida; senão a mais recente (index vem ordenado por created_at desc)
    activeExec.value =
      execs.find(e =>
        ['running', 'waiting', 'waiting_response'].includes(e.status)
      ) ||
      execs[0] ||
      null;
    if (activeExec.value) {
      selectedFlowId.value = activeExec.value.flow_id;
      if (!flowsById.value[activeExec.value.flow_id]) {
        const f = (await axios.get(`${base}/flows/${activeExec.value.flow_id}`))
          .data.data;
        flowsById.value[f.id] = f;
      }
    }
  } catch (e) {
    /* noop */
  } finally {
    loading.value = false;
  }
};

const isActive = computed(() =>
  ['running', 'waiting', 'waiting_response'].includes(statusKey.value)
);
const isPaused = computed(() => statusKey.value === 'paused');

const onSelect = async () => {
  if (!selectedFlowId.value) return;
  // QA #40: associar uma sequência dispara mensagem ao cliente na hora — confirma
  // antes, senão um clique errado manda mensagem.
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CHATBOT.flowBox.confirmAssociate'))) {
    // reset no próximo tick: o <select> nativo já commitou a escolha do usuário
    // durante o @change, então zerar só pega depois que o evento assenta.
    selectedFlowId.value = '';
    nextTick(() => {
      selectedFlowId.value = '';
    });
    return;
  }
  saving.value = true;
  try {
    await axios.post(`${base}/flow_executions`, {
      flow_id: selectedFlowId.value,
      conversation_id: props.conversationId,
    });
    await loadAll();
  } finally {
    saving.value = false;
  }
};

const pauseExec = async () => {
  await axios.patch(`${base}/flow_executions/${activeExec.value.id}/pause`);
  await loadAll();
};
const resumeExec = async () => {
  await axios.patch(`${base}/flow_executions/${activeExec.value.id}/resume`);
  await loadAll();
};
const removeExec = async () => {
  if (!window.confirm(t('CHATBOT.flowBox.confirmRemove'))) return;
  await axios.delete(`${base}/flow_executions/${activeExec.value.id}`);
  selectedFlowId.value = '';
  await loadAll();
};

onMounted(loadAll);
</script>

<template>
  <div class="p-3 text-sm">
    <div v-if="loading" class="text-n-slate-10">
      {{ t('CHATBOT.flowBox.loading') }}
    </div>
    <template v-else>
      <label class="block mb-1 text-xs font-medium text-n-slate-11">{{
        t('CHATBOT.flowBox.associate')
      }}</label>
      <select
        v-model="selectedFlowId"
        class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 disabled:opacity-60"
        :disabled="saving"
        @change="onSelect"
      >
        <option value="">{{ t('CHATBOT.flowBox.selectSequence') }}</option>
        <option v-for="s in sequences" :key="s.id" :value="s.id">
          {{ s.name }}
        </option>
      </select>
      <p v-if="!sequences.length" class="mt-1.5 text-xs text-n-slate-10">
        {{ t('CHATBOT.flowBox.noSequences') }}
      </p>

      <template v-if="activeExec && activeFlow">
        <button
          class="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="showJourney = true"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          {{ t('CHATBOT.flowBox.viewJourney') }}
        </button>
        <p
          v-if="isActive && currentNodeLabel"
          class="mt-1.5 text-xs text-n-slate-10"
        >
          {{ t('CHATBOT.flowBox.currentlyAt') }}
          <span class="font-medium text-n-slate-12">{{
            currentNodeLabel
          }}</span>
        </p>
        <p v-else class="mt-1.5 text-xs text-n-slate-10">
          {{
            isPaused
              ? t('CHATBOT.flowBox.pausedHint')
              : t('CHATBOT.flowBox.lastStatus')
          }}
          <span class="font-medium text-n-slate-12">{{
            t(`CHATBOT.flowBox.status.${statusKey}`)
          }}</span>
        </p>

        <!-- pausar / retomar / remover este usuário do flow -->
        <div v-if="isActive || isPaused" class="flex flex-wrap gap-2 mt-2">
          <button
            v-if="isActive"
            class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-amber-11 bg-n-background hover:bg-n-alpha-1"
            @click="pauseExec"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
            </svg>
            {{ t('CHATBOT.flowBox.pause') }}
          </button>
          <button
            v-if="isPaused"
            class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-teal-11 bg-n-background hover:bg-n-alpha-1"
            @click="resumeExec"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            {{ t('CHATBOT.flowBox.resume') }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-n-ruby-6 text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
            @click="removeExec"
          >
            <svg
              class="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            {{ t('CHATBOT.flowBox.remove') }}
          </button>
        </div>
      </template>
    </template>

    <FlowJourneyModal
      v-if="showJourney && activeFlow"
      :flow-name="activeFlow.name"
      :flow-data="activeFlow.flow_data"
      :current-node-id="activeExec?.current_node_id"
      @close="showJourney = false"
    />
  </div>
</template>
